import { StreamHandler } from "./stream.handler";
import {
  AckNotifications,
  SubscribeNotifications,
  UpdateNotifications,
  RESULT,
} from "@/types";
import type {
  PrizeDropHotPeriodNotificationSchema,
  PrizeDropRevealedNotificationSchema,
  PrizeDropWonNotificationSchema,
  WebSocketResponse,
} from "@/types";
import type { NotificationSchema } from "@/services/notifications";
import {
  ackFlushSettled,
  ackNotifications,
  applyBetFeedback,
  applyPrizeDisclosure,
  enqueueWonNotifications,
  isBetFeedbackNotification,
  ownsInGameNotifications,
  pruneAckedWonPrizes,
  retryPendingAcks,
} from "@/services/notifications";
import {
  isMissionNotification,
  showMissionNotifications,
} from "@/services/mission-notifications";
import {
  isJackpotWonNotification,
  showJackpotNotifications,
} from "@/services/jackpot-notifications";
import {
  isTournamentNotification,
  showTournamentNotifications,
} from "@/services/tournament-notifications";
import {
  isChaseNotification,
  showChaseNotifications,
} from "@/services/chase-notifications";
import { useAppStore } from "@/stores";

/**
 * `notifications` — the player-notification stream. `prize_drop_won` is queued
 * for the reveal flow and acked only once the player confirms each prize. The
 * in-game kinds — the mission types, `jackpot_won` and the tournament results —
 * are toasted by the surface that owns the in-game notifications and acked by
 * it on receipt; see `apply` for the surfaces that don't own them. The two chase
 * types are toasted on every surface and acked on receipt (informational —
 * nothing gates on the player seeing them). Every other type — including ones
 * this build doesn't know yet — is acked on receipt and dropped, so undisplayed
 * notifications never pile up server-side.
 */
export class NotificationsHandler extends StreamHandler<NotificationSchema[]> {
  protected readonly subscribeType = SubscribeNotifications;
  protected readonly updateType = UpdateNotifications;

  protected isBusy(): boolean {
    const s = useAppStore.getState().notificationsStream;
    return s.subscribed || s.loading;
  }
  protected setLoading(loading: boolean): void {
    useAppStore.getState().setNotificationsLoading(loading);
  }
  protected setSubscribed(subscribed: boolean): void {
    useAppStore.getState().setNotificationsSubscribed(subscribed);
    // Acks staged while the socket was down — or whose response died with the
    // old socket — retry here; this runs on every successful (re)subscribe.
    if (subscribed) retryPendingAcks();
  }
  protected clear(): void {
    useAppStore.getState().clearNotifications();
  }

  protected apply(data: NotificationSchema[], initial: boolean): void {
    // Narrow on the `type` discriminant, not on a structural `Extract<…>`: the
    // won-notification's `prizes` changed shape (string[] -> objects carrying
    // the ack drop_key), which silently collapsed a structural Extract to
    // `never` — still compiling, but with no type-checking left on the array.
    const won = data.filter(
      (n): n is PrizeDropWonNotificationSchema => n.type === "prize_drop_won"
    );
    // The post-reveal disclosure (#3922) — the prize identities the won payload withholds.
    // Held, not acked on receipt: it is what the open reveal renders from, and it is acked when
    // the player dismisses that reveal (see dismissRevealedPrize).
    const revealed = data.filter(
      (n): n is PrizeDropRevealedNotificationSchema =>
        n.type === "prize_drop_revealed"
    );
    // The in-game kinds wait for the surface that toasts them: a lobby widget
    // acking one it never shows is a result the player never hears about (a
    // tournament ending while no game is open). Only once the brand runs the
    // docked overlay (`dockedOverlaySince`) — the legacy overlay keeps its own
    // copies and never reads these, so before the cutover nobody else would
    // ever ack them.
    const state = useAppStore.getState();
    const leaveInGame =
      !ownsInGameNotifications(state) && state.dockedOverlaySince !== null;
    const isInGame = (n: NotificationSchema) =>
      isMissionNotification(n) ||
      isJackpotWonNotification(n) ||
      isTournamentNotification(n);
    // One created before the cutover was the legacy overlay's to show (it has
    // its own copy): acked like the rest, but not announced again.
    // CUTOVER BRIDGE (#4118): `dockedOverlaySince` goes once every tenant is on v3.
    const since = state.dockedOverlaySince
      ? Date.parse(state.dockedOverlaySince)
      : null;
    const announced =
      since === null
        ? data
        : data.filter((n) => !isInGame(n) || Date.parse(n.created_at) >= since);
    // Ack everything else we don't display (incl. unknown future types) on receipt — bet
    // feedback excepted: it is state the backend cancels, never acked.
    ackNotifications(
      data
        .filter(
          (n) =>
            n.type !== "prize_drop_won" &&
            n.type !== "prize_drop_revealed" &&
            !isBetFeedbackNotification(n) &&
            !(leaveInGame && isInGame(n))
        )
        .map((n) => n.id)
    );
    applyHotPeriods(
      data.filter(
        (n): n is PrizeDropHotPeriodNotificationSchema =>
          n.type === "prize_drop_hot_period"
      )
    );
    applyPrizeDisclosure(revealed);
    applyBetFeedback(data);
    enqueueWonNotifications(won);
    // Reconcile out anything locally queued that a DIFFERENT surface (the legacy overlay) has
    // already resolved — this payload is the backend's complete current pending set.
    pruneAckedWonPrizes(won);
    showMissionNotifications(announced.filter(isMissionNotification));
    showJackpotNotifications(announced.filter(isJackpotWonNotification));
    showTournamentNotifications(announced.filter(isTournamentNotification));
    // `initial` = the subscribe page, i.e. a replay of everything still within
    // the rows' 300s TTL; the toast layer decides how much of that is still
    // worth showing (see BACKLOG_FRESHNESS_MS).
    showChaseNotifications(data.filter(isChaseNotification), initial);
  }

  // The stream also answers our acks; surface failures instead of dropping
  // them silently (an un-acked notification would be re-delivered forever).
  canHandle(messageType: string): boolean {
    return messageType === AckNotifications || super.canHandle(messageType);
  }

  async handle(message: WebSocketResponse): Promise<void> {
    if (message.type === AckNotifications) {
      const ok = message.result === RESULT.OK;
      if (!ok) this.logger.error("Notification ack failed", message);
      // Settle the in-flight batch: OK unstages it, an error keeps it staged
      // for the next resubscribe (see flushPendingAcks).
      ackFlushSettled(ok);
      return;
    }
    return super.handle(message);
  }
}

/** How long a hot period lasts on the button: the notification's own TTL
 *  (`HotPeriodNotification.default_ttl`, backend). */
const HOT_PERIOD_MS = 180_000;

/** A prize drop's hot period (acked on receipt like every hook the widget
 *  doesn't list; the button only needs until when it runs hot). A replayed
 *  one past its time is dropped. */
function applyHotPeriods(hot: PrizeDropHotPeriodNotificationSchema[]): void {
  const store = useAppStore.getState();
  for (const n of hot) {
    const until = Date.parse(n.created_at) + HOT_PERIOD_MS;
    if (until > Date.now()) store.setPrizeDropHot(n.campaign_id, until);
  }
}

export const notificationsHandler = new NotificationsHandler();
